import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Crop, PictureInPicture2, Gauge, Lock } from 'lucide-react';

const Row = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs text-[#8A93A6]">{label}</label>
    {children}
  </div>
);
const SingleSlider = ({ value, onChange, min = 0, max = 200, step = 1, testId }) => (
  <div className="flex items-center gap-3">
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="flex-1" data-testid={testId} />
    <span className="text-xs text-white w-12 text-right">{Math.round(value)}</span>
  </div>
);

const REFRAME_RATIOS = ['9:16', '1:1', '4:5', '16:9'];

/**
 * Video-item controls: Transform (scale/position/rotation/opacity), Crop,
 * Playback Speed, Picture-in-Picture, and Automatic Reframing. Reframing
 * only ever exposes configuration + an honest "pending integration"
 * status — no subject-aware media-analysis service exists in this
 * codebase, so it is never marked as a completed/applied result here.
 */
export function EditorVideoControls({ item, onTransformChange, onCropChange, onSpeedChange, onTogglePip, onToggleReframing }) {
  const transform = item.transform || { scale_percent: 100, position_x: 0, position_y: 0, rotation_degrees: 0, opacity_percent: 100 };
  const crop = item.crop || { x: 0, y: 0, width: 100, height: 100 };
  const speed = item.speed_percent ?? 100;
  const reframing = item.reframing || { enabled: false, target_aspect_ratio: '9:16', status: 'not_requested' };

  return (
    <div className="space-y-4 pt-2 border-t border-white/5" data-testid="video-controls-panel">
      <p className="text-xs text-[#555E6E] uppercase tracking-wide">Transform</p>
      <Row label="Scale (%)"><SingleSlider value={transform.scale_percent ?? 100} min={10} max={300} onChange={(v) => onTransformChange({ scale_percent: v })} testId="inspector-scale-slider" /></Row>
      <Row label="Position X"><SingleSlider value={transform.position_x ?? 0} min={-960} max={960} onChange={(v) => onTransformChange({ position_x: v })} testId="inspector-position-x-slider" /></Row>
      <Row label="Position Y"><SingleSlider value={transform.position_y ?? 0} min={-540} max={540} onChange={(v) => onTransformChange({ position_y: v })} testId="inspector-position-y-slider" /></Row>
      <Row label="Rotation (°)"><SingleSlider value={transform.rotation_degrees ?? 0} min={-180} max={180} onChange={(v) => onTransformChange({ rotation_degrees: v })} testId="inspector-rotation-slider" /></Row>
      <Row label="Opacity (%)"><SingleSlider value={transform.opacity_percent ?? 100} min={0} max={100} onChange={(v) => onTransformChange({ opacity_percent: v })} testId="inspector-opacity-slider" /></Row>

      <div className="flex items-center justify-between pt-1">
        <span className="flex items-center gap-1.5 text-xs text-[#8A93A6]"><PictureInPicture2 className="h-3.5 w-3.5" />Picture-in-Picture</span>
        <Switch checked={!!item.is_pip} onCheckedChange={onTogglePip} data-testid="inspector-pip-switch" />
      </div>

      <div className="pt-2 border-t border-white/5 space-y-3">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Crop className="h-3 w-3" />Crop</p>
        <div className="grid grid-cols-2 gap-3">
          <Row label="X (%)"><SingleSlider value={crop.x ?? 0} min={0} max={99} onChange={(v) => onCropChange({ ...crop, x: Math.min(v, 100 - (crop.width ?? 100)) })} testId="inspector-crop-x-slider" /></Row>
          <Row label="Y (%)"><SingleSlider value={crop.y ?? 0} min={0} max={99} onChange={(v) => onCropChange({ ...crop, y: Math.min(v, 100 - (crop.height ?? 100)) })} testId="inspector-crop-y-slider" /></Row>
          <Row label="Width (%)"><SingleSlider value={crop.width ?? 100} min={1} max={100} onChange={(v) => onCropChange({ ...crop, width: Math.min(v, 100 - (crop.x ?? 0)) })} testId="inspector-crop-width-slider" /></Row>
          <Row label="Height (%)"><SingleSlider value={crop.height ?? 100} min={1} max={100} onChange={(v) => onCropChange({ ...crop, height: Math.min(v, 100 - (crop.y ?? 0)) })} testId="inspector-crop-height-slider" /></Row>
        </div>
      </div>

      <div className="pt-2 border-t border-white/5 space-y-2">
        <p className="text-xs text-[#555E6E] uppercase tracking-wide flex items-center gap-1.5"><Gauge className="h-3 w-3" />Playback Speed</p>
        <Row label={`${(speed / 100).toFixed(2)}x`}><SingleSlider value={speed} min={25} max={400} onChange={onSpeedChange} testId="inspector-speed-slider" /></Row>
      </div>

      <div className="pt-2 border-t border-white/5 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#8A93A6]">Automatic Reframing</span>
          <Switch checked={!!reframing.enabled} onCheckedChange={(v) => onToggleReframing(v, reframing.target_aspect_ratio)} data-testid="inspector-reframe-switch" />
        </div>
        {reframing.enabled && (
          <>
            <Select value={reframing.target_aspect_ratio} onValueChange={(ar) => onToggleReframing(true, ar)}>
              <SelectTrigger className="h-8 bg-[#1C1F26] border-white/10 text-white text-sm" data-testid="inspector-reframe-ratio-select"><SelectValue /></SelectTrigger>
              <SelectContent>{REFRAME_RATIOS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <p className="flex items-center gap-1 text-[10px] text-amber-400/80" data-testid="inspector-reframe-status">
              <Lock className="h-3 w-3" />Pending processing integration — no subject-aware analysis service connected yet.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
